import posthog from "posthog-js";
import {
  Search,
  Bell,
  ArrowUp,
  Zap,
  Menu,
  X,
  User,
  FileText,
  KeyRound,
  LogOut,
  ChevronDown,
  CirclePoundSterling,
  HandCoins,
  MessageSquare,
  CheckCircle2,
  GraduationCap,
  Compass,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { generateApiKey } from "../../../../helpers/common.helpers";
import FraudNotificationStrip from "../../../Dashboard/FraudNotificationStrip";
import FeedbackForm from "./FeedbackForm";
import { fetchApplicationsService, fetchUserFeedbackCount, fetchAcknowledgmentStatusService, resetDashboardTourService } from "../../../../services/users.services";
import SecurityAdvisoryModal from "../../../Myaccount/acknoledgement-modal";
import { useMutation, useQuery } from "@tanstack/react-query";
import ExpectationsModal from "../../../Dashboard/ExpectationsModal";

// University logos mapping
const UNIVERSITY_LOGOS = {
  'iitg': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/logo/iitg-logo.webp",
  'iimv': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/other/iimv-logo-v5.png",
  'xlri': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/other/xlri-logo.jpg",
  'spjain': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/logo/soj-brochure-logo.webp",
  'iitk': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/logo/iit-kanpur-logo-round.webp",
  'iimt': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/other/IIMT-H.webp",
  'iiml': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/logo/iiml-lucknow-logo-100x100.webp",
  'iima': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/logo/iim-amritsar-round-logo.webp",
  'iith': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/other/iith-short-logo.webp",
  'unknown': "https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/logo/accredian-logo.webp"
};

const UNIVERSITY_FULL_NAMES = {
  'iitg': 'E&ICT Academy IIT Guwahati',
  'spjain': 'SP Jain School of Global Management',
  'iimv': 'IIM Visakhapatnam',
  'xlri': 'XLRI',
  'iimt': 'IIM Tiruchirappalli',
  'iitk': 'E&ICT Academy IIT Kanpur',
  'iiml': 'IIM Lucknow',
  'iima': 'IIM Amritsar',
  'unknown': 'Independent Program'
};

const stripUniversityFromProgramName = (programName = '') => {
  if (!programName) return '';

  // 1) If it has " - " (IIM V - Senior Management Program)
  const dashIndex = programName.indexOf(' - ');
  if (dashIndex !== -1) {
    return programName.slice(dashIndex + 3).trim();
  }

  // 2) If it starts with a short prefix like "IIT G " or "IIM Trichy "
  //    remove first word or first two words heuristically
  const words = programName.split(' ');
  if (words.length > 2) {
    // handle patterns like "IIT G Executive ..." or "IIM Trichy Accelerated ..."
    if (words[0] === 'IIT' || words[0] === 'IIM' || words[0] === 'XLRI' || words[0] === 'IIMV' || words[0] === 'IITG' || words[0] === 'IITK') {
      // For "IIT G", it's two tokens. For "XLRI", it's one.
      // Let's check for specific multi-word prefixes
      if (words[0] === 'IIT' && (words[1] === 'G' || words[1] === 'K')) {
        return words.slice(2).join(' ').trim();
      }
      return words.slice(1).join(' ').trim();
    }
  }

  // Common single-word prefixes without dash
  const commonPrefixes = ['IITG', 'IITK', 'XLRI', 'IIMV', 'IIMT', 'IIML', 'IIMA'];
  for (const prefix of commonPrefixes) {
    if (programName.startsWith(prefix)) {
      return programName.replace(prefix, '').trim();
    }
  }

  return programName;
};

// Custom Button Component to replace Shadcn Button
const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  const variantClasses = {
    default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    ghost: "hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-300",
    outline:
      "border border-gray-300 bg-transparent hover:bg-gray-50 focus:ring-gray-300",
  };

  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
    icon: "h-10 w-10",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Custom Badge Component to replace Shadcn Badge
const Badge = ({ children, className = "" }) => {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white ${className}`}
    >
      {children}
    </span>
  );
};

// Custom Avatar Components to replace Shadcn Avatar
const Avatar = ({ className = "", children }) => {
  return (
    <div
      className={`relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full ${className}`}
    >
      {children}
    </div>
  );
};

const AvatarFallback = ({ className = "", children }) => {
  return (
    <div
      className={`flex h-full w-full items-center justify-center rounded-full bg-gray-100 text-gray-600 ${className}`}
    >
      {children}
    </div>
  );
};

// TopBar Component with API integration
const TopBar = ({ isMobile, onMobileMenuToggle }) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [countNoti, setCountNoti] = useState(0);
  const [coinsdata, setCoindata] = useState(0);
  const [isEnrollmentOpen, setIsEnrollmentOpen] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [currentEnrollment, setCurrentEnrollment] = useState(null);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [expectationsModalOpen, setExpectationsModalOpen] = useState(false);
  const [updateExpectationsCount, setExpecationsUpdateCount] = useState(true);
  const [showExpectationButton, setShowExpectationButton] = useState(false);
  const [acknowledgementModalOpen, setAcknowledgementModalOpen] = useState(false);
  const [isTourMenuOpen, setIsTourMenuOpen] = useState(false);

  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const bellRef = useRef(null);
  const enrollmentRef = useRef(null);
  const mobileEnrollmentRef = useRef(null);
  const tourMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isLocked = localStorage.getItem("status") !== "active";

  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab");
  const shouldHideUpgradeAndNotifications =
    tabParam === "my-courses" || tabParam === "my-applications" || location.pathname === "/studentDetails" || localStorage.getItem("category") == "undefined";

  // Query to fetch applications
  const { data: applicationsData } = useQuery({
    queryFn: () => fetchApplicationsService({ user_id: localStorage.getItem("user_id") }),
    queryKey: ["applications-topbar"],
    enabled: !!localStorage.getItem("user_id"),
  });

  // Query to fetch acknowledgment status
  const { data: acknowledgmentData } = useQuery({
    queryFn: () => fetchAcknowledgmentStatusService(),
    queryKey: ["acknowledgment-status"],
    enabled: !!localStorage.getItem("user_id"),
  });

  useEffect(() => {
    if (acknowledgmentData && acknowledgmentData?.security_status === 0) {
      setAcknowledgementModalOpen(true);
    } else {
      setAcknowledgementModalOpen(false);
    }
  }, [acknowledgmentData]);

  // Notify the rest of the app whenever a blocking modal opens/closes
  // so DashboardTour can wait for ALL of them to be closed
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tourBlockingModalChange', {
      detail: { type: 'acknowledgement', isOpen: acknowledgementModalOpen }
    }));
  }, [acknowledgementModalOpen]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tourBlockingModalChange', {
      detail: { type: 'expectations', isOpen: expectationsModalOpen }
    }));
  }, [expectationsModalOpen]);

  const { mutate: feedBackCountUser, isPending: isFetching } = useMutation({
    mutationFn: ({ userId }) =>
      fetchUserFeedbackCount({ userId }),
    onSuccess: (data) => {
      // queryClient.invalidateQueries(["acknowledgment-status"]);
      // setOpen(false);
      console.log(data)
      localStorage.setItem("feedback_count", data.feedback_count);
      localStorage.setItem("dashboardFeedbackStatus", data.feedback_status);
    },
    onError: (error) => {
      console.error("Error updating acknowledgment status:", error);
    }
  });

  // Fetch Token and User Details
  const fetchToken = async () => {
    const token = document.cookie.replace(
      /(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/,
      "$1"
    );
    if (!token) return false;

    const options = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        authorization: token,
        "Page-Index": generateApiKey(),
      },
    };

    try {
      const result = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/verifyToken/token`,
        {},
        options
      );
      if (result.data.status === 200) {
        const user_data = result.data.data;
        if (user_data.user_id) localStorage.setItem("user_id", user_data.user_id);
        if (user_data.firstname) localStorage.setItem("username", user_data.firstname);
        if (user_data.userlastname) localStorage.setItem("lastname", user_data.userlastname);
        if (user_data.email) localStorage.setItem("email", user_data.email);
        if (user_data.mobile) localStorage.setItem("mobile", user_data.mobile);
        if (user_data.count !== undefined) {
          localStorage.setItem("count", user_data.count);
        }
        return true;
      }
    } catch (e) {
      console.error("Token verification failed", e);
    }
    return false;
  };

  // Fetch enrollments
  const fetchEnrollments = async () => {
    try {
      const token = document.cookie.replace(
        /(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/,
        "$1"
      );

      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          authorization: token,
          "Page-Index": generateApiKey(),
        },
      };

      const sendData = {
        user_id: localStorage.getItem("user_id"),
      };

      const result = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/fetchenrolments`,
        JSON.stringify(sendData),
        options
      );

      if (result.data && result.data.length > 0) {
        // Enhance enrollments with university data from applications
        const enhancedEnrollments = result.data.map((enrollment) => {
          const appMatch = applicationsData?.applications?.find(
            (app) => app.program_id === enrollment.program_id &&
              app.batch_id === enrollment.batch_id
          );

          return {
            ...enrollment,
            university: appMatch?.university || 'unknown'
          };
        });

        setEnrollments(enhancedEnrollments);

        // Check if enrollment is already set in localStorage
        const savedEnrolId = localStorage.getItem("enrol_id");
        let selectedEnrollment;

        if (savedEnrolId && savedEnrolId !== "undefined" && savedEnrolId !== "null") {
          // Find the saved enrollment
          selectedEnrollment = enhancedEnrollments.find(
            e => e.enrol_id === parseInt(savedEnrolId)
          );
        }

        // If no saved enrollment or not found, use first one
        if (!selectedEnrollment) {
          selectedEnrollment = enhancedEnrollments[0];

          // Mimic Myaccount data setting
          localStorage.setItem("count", enhancedEnrollments.length);
          updateLocalStorageForEnrollment(selectedEnrollment, false);

          window.location.reload();
        } else {
          // Even if selected, ensure count is up to date
          localStorage.setItem("count", enhancedEnrollments.length);

          // ALWAYS sync status from the enrollment API — it is the authoritative source.
          // localStorage.status can be stale or "undefined" if verifyToken didn't return it.
          if (selectedEnrollment.status !== undefined && selectedEnrollment.status !== null) {
            localStorage.setItem("status", selectedEnrollment.status);
          }
        }

        setCurrentEnrollment(selectedEnrollment);
      }
      setLoadingEnrollments(false);
    } catch (error) {
      console.error("Error fetching enrollments:", error);
      setLoadingEnrollments(false);
    }
  };

  useEffect(() => {
    if (applicationsData?.applications && enrollments.length > 0) {
      const enhancedEnrollments = enrollments.map((enrollment) => {
        const appMatch = applicationsData.applications.find(
          (app) => app.program_id === enrollment.program_id &&
            app.batch_id === enrollment.batch_id
        );

        return {
          ...enrollment,
          university: appMatch?.university || enrollment.university || 'unknown'
        };
      });

      setEnrollments(enhancedEnrollments);

      // Update current enrollment if it exists
      if (currentEnrollment) {
        const updatedCurrent = enhancedEnrollments.find(e => e.enrol_id === currentEnrollment.enrol_id);
        if (updatedCurrent) {
          setCurrentEnrollment(updatedCurrent);
        }
      }
    }
  }, [applicationsData]);

  const updateLocalStorageForEnrollment = (enrollment, triggerEvent = true) => {
    console.log("Status set from top bar " + enrollment.status)
    // Guard: never overwrite status with undefined/null — preserve existing value
    if (enrollment.status !== undefined && enrollment.status !== null) {
      localStorage.setItem("status", enrollment.status);
    }
    localStorage.setItem("enrol_id", enrollment.enrol_id);
    localStorage.setItem("category", enrollment.category);
    localStorage.setItem("program_id", enrollment.program_id);
    localStorage.setItem("term", enrollment.term);
    localStorage.setItem("program_name", enrollment.program_name);
    localStorage.setItem("program_code", enrollment.program_code);
    localStorage.setItem("batch_id", enrollment.batch_id);
    localStorage.setItem("batch", enrollment.batch_date);
    localStorage.setItem("is_completed", enrollment.is_completed);
    localStorage.setItem("inclusion_link", enrollment.inclusion_link);
    localStorage.setItem("university", enrollment.university);

    // Trigger storage event for other components to listen
    if (triggerEvent) {
      window.dispatchEvent(new Event('enrollmentChanged'));
    }
  };


  const handleEnrollmentChange = (enrollment) => {
    setCurrentEnrollment(enrollment);
    updateLocalStorageForEnrollment(enrollment, true);
    setIsEnrollmentOpen(false);

    // Reload page to sync all components
    window.location.reload();
  };



  const getStatusBadge = (status, isCompleted) => {
    if (isCompleted === 1) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </span>
      );
    }

    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Active</span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Inactive</span>;
      case 'suspended':
        return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">Suspended</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  // Fetch notifications from API
  const fetchNotificationData = async () => {
    try {
      const res = await axios({
        method: "post",
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          // Assuming generateApiKey is defined elsewhere if needed
          "page-index": generateApiKey(),
        },
        url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/fetchnotification`,
        data: {
          enrol_id: Number(localStorage.getItem("enrol_id")),
        },
      });
      setNotifications(res.data.notification || []);
      setCountNoti(res.data.unchecked_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const fetchcoindata = async () => {
    await axios({
      method: "post",
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        "page-index": generateApiKey(),
      },
      url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/fetchstudentcoins`,
      data: {
        enrol_id: localStorage.getItem("enrol_id"),
      },
    }).then((res) => {
      setCoindata(res.data.toString());
      localStorage.setItem("coins", res.data.toString());
    });
  };

  // Mark notification as checked
  const checkdClick = async (id) => {
    try {
      const res = await axios({
        method: "post",
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
        url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/updatenotification_status`,
        data: {
          enrol_id: 38626,
          id: id,
        },
      });
      setCountNoti(res.data.unchecked_count || 0);
      // Optionally update the checked state locally
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, checked: 1 } : n))
      );
    } catch (error) {
      console.error("Failed to update notification status", error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      let userId = localStorage.getItem("user_id");

      // If user_id is missing, try to fetch it via token
      if (!userId || userId === "null" || userId === "undefined") {
        const success = await fetchToken();
        if (success) {
          userId = localStorage.getItem("user_id");
        }
      }

      if (userId) {
        feedBackCountUser({ userId });
        fetchEnrollments();
        fetchNotificationData();
        fetchcoindata();
      }
    };

    initialize();
  }, []);

  // Close profile menu and notification popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        profileButtonRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }
      if (
        bellRef.current &&
        !bellRef.current.contains(event.target) &&
        !isMobile
      ) {
        setShowNotifications(false);
      }
      const clickedDesktop = enrollmentRef.current && enrollmentRef.current.contains(event.target);
      const clickedMobile = mobileEnrollmentRef.current && mobileEnrollmentRef.current.contains(event.target);

      if (!clickedDesktop && !clickedMobile) {
        setIsEnrollmentOpen(false);
      }

      if (tourMenuRef.current && !tourMenuRef.current.contains(event.target)) {
        setIsTourMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile]);

  const fetchBatchInaugurationDate = async (category_id, program_id, batch_id) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/fetchBatchInaugurationDate`,
        { category_id: category_id, program_id: program_id, batch_id: batch_id },

      );
      if (response.data.status === 200 && response.data.data.length > 0) {
        return response.data.data[0].inauguration_date;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const shouldShowExpectationsModal = async (enrollment) => {
    if (!enrollment) return false;

    // Only show for active or under_review — not for registered users
    if (enrollment.status !== 'active' && enrollment.status !== 'under_review') {
      return false;
    }

    // Logic adapted for single enrollment
    const cutoffDate = new Date('2025-10-15T00:00:00Z');
    const currentDate = new Date();

    const batchDate = new Date(enrollment.batch_date);
    // If batch starts before cutoff, skip
    if (batchDate <= cutoffDate) {
      return false;
    }

    let shouldShow = false;

    // Fetch inauguration date for this batch
    const inaugurationDate = await fetchBatchInaugurationDate(enrollment.category, enrollment.program_id, enrollment.batch_id);
    console.log(inaugurationDate, "inaugurationDate")
    let deadlineDate;
    if (inaugurationDate) {
      deadlineDate = new Date(inaugurationDate);
    } else {
      deadlineDate = batchDate;
    }

    if (currentDate <= deadlineDate) {
      shouldShow = true;
    }

    if (!shouldShow) return false;

    try {
      const userId = localStorage.getItem("user_id");
      // Check backend skip count
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/fetchExpectationSkipStatus`,
        { user_id: userId }
      );

      if (response.data.status === 200) {
        // const backendSkipCount = Number(response.data.data.expectation_skip_status) || 0;
        const student_expectation_status = Number(response.data.data.student_expectation_status) || 0;

        // const localSkipCount = parseInt(localStorage.getItem('expectation_skip_count') || '0');
        // if (localSkipCount > 0) return false;

        if (student_expectation_status === 1) return false;

        // Check if expectations appear filled already
        const detailsResponse = await axios.post(
          `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/fetchIITKUserDetails`,
          { user_id: userId }
        );

        if (detailsResponse.data.status === 200) {
          const userData = detailsResponse.data.data[0];
          if (userData.expectations && userData.challenges && userData.expert_focus && userData.consent === '1') {
            return false;
          }
        }
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
    return true;
  };

  // PostHog identify – fires on initial login and whenever the enrollment changes
  useEffect(() => {
    if (!currentEnrollment) return;

    const userId = localStorage.getItem("user_id");
    const email = localStorage.getItem("email");
    const firstName = localStorage.getItem("username");
    const lastName = localStorage.getItem("lastname");

    if (!userId || !email) return;

    posthog.identify(userId, {
      email,
      name: [firstName, lastName].filter(Boolean).join(" "),
      // Enrollment details
      current_program: currentEnrollment.program_name,
      program_code: currentEnrollment.program_code,
      category: currentEnrollment.category,
      university: currentEnrollment.university,
    });
  }, [currentEnrollment]);

  useEffect(() => {
    const checkExpectations = async () => {
      if (currentEnrollment) {
        // Only show expectations for active or under_review — skip registered
        if (currentEnrollment.status !== 'active' && currentEnrollment.status !== 'under_review') {
          setShowExpectationButton(false);
          setExpectationsModalOpen(false);
          return;
        }

        const shouldShow = await shouldShowExpectationsModal(currentEnrollment);
        console.log("shouldShow", shouldShow);
        setShowExpectationButton(shouldShow);

        const localSkipCount = parseInt(localStorage.getItem('expectation_skip_count') || '0');
        if (shouldShow && localSkipCount === 0) {
          setExpectationsModalOpen(true);
        } else {
          setExpectationsModalOpen(false);
        }
      }
    };
    checkExpectations();
  }, [currentEnrollment]);

  // Check for student details sync and expectations redirection
  useEffect(() => {
    const checkStudentDetailsRedirect = async () => {
      if (!enrollments.length || location.pathname === "/studentDetails" || tabParam === 'my-applications') return;

      const studentDetailsSyncBatchIds = [103, 110, 112, 116, 123];
      const cutoffDate = new Date('2025-10-15T00:00:00Z');

      const syncStatus = studentDetailsSyncBatchIds.includes(enrollments[0]?.batch_id);

      const expectationStatus = enrollments.some(enrol => {
        const batchDate = new Date(enrol.batch_date);
        return batchDate > cutoffDate;
      });

      if (syncStatus || expectationStatus) {
        // Only redirect to studentDetails for active/under_review — skip registered users
        const hasEligibleStatus = enrollments.some(
          enrol => enrol.status === 'active' || enrol.status === 'under_review'
        );
        if (!hasEligibleStatus) return;

        try {
          const userId = localStorage.getItem("user_id");
          const res = await axios.post(
            `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/fetchIITKUserDetails`,
            { user_id: userId }
          );

          if (res.data.status === 200 && res.data.data.length > 0) {
            const {
              address1, city, country, current_organization, dob, gender,
              mobile, pincode, qualification, university, industry,
              designation, work_ex
            } = res.data.data[0];

            if (!(
              address1 && city && country && current_organization && dob &&
              gender && mobile && pincode && qualification && university &&
              industry && designation && work_ex
            )) {
              navigate(`/studentDetails?expectations=${expectationStatus ? 'true' : 'false'}`);
            }
          }
        } catch (error) {
          console.error("Error checking IITK user details:", error);
        }
      }
    };

    checkStudentDetailsRedirect();
  }, [enrollments, location.pathname, tabParam, navigate]);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleRestartTour = async () => {
    try {
      await resetDashboardTourService();
    } catch (e) {
      // non-blocking – even if reset fails, fire the event
      console.error("Tour reset failed:", e);
    }
    window.dispatchEvent(new CustomEvent('restartDashboardTour'));
  };

  const LogOutUser = (name, domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
    localStorage.clear();
    window.location.href = "https://accredian.com";
  };

  const handleProfileMenuClick = (action) => {
    console.log(`${action} clicked`);
    setIsProfileMenuOpen(false);

    switch (action) {
      case "notifications":
        if (isMobile) {
          setShowNotificationsModal(true);
        }
        break;
      case "profile":
        navigate("/Profile");
        break;
      case "policy":
        window.open(
          "https://accredian.com/policies/accredian-policy?_gl=1*1lt8sky*_gcl_au*MjI2MzYxNzk4LjE3NTg3ODc3NjMuOTI2NzU2OTg2LjE3NTkzMTYxODUuMTc1OTMxNjE4NA..*_ga*MTk2NDAwMDQ3MC4xNzUxMDA1Njc3*_ga_SWCZ0464KL*czE3NTkzMTYxMzEkbzMxNCRnMSR0MTc1OTMxNjE4NCRqNyRsMCRoMA..",
          "_blank"
        );
        break;
      case "password":
        navigate("/ChangePassword");
        break;
      case "logout":
        LogOutUser("token", "accredian.com");
        // Add logout logic here as needed
        break;
      default:
        break;
    }
  };

  const showFeedback = !shouldHideUpgradeAndNotifications && !isLocked;
  const showExpectations = showExpectationButton && !isLocked;
  const isBothVisible = showFeedback && showExpectations;


  return (
    <>
      <div className="relative z-[250] flex items-center justify-between p-4 bg-white border-b border-gray-200 shadow-sm">
        {/* Left side - Mobile menu button and Enrollment Dropdown */}
        <div className="flex items-center gap-4">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMobileMenuToggle}
              className="md:hidden p-2 hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-20 h-20" />
            </Button>
          )}

          {/* Enrollment Dropdown - LEFT SIDE */}
          {!loadingEnrollments && enrollments.length > 0 && currentEnrollment && (
            <div className="relative hidden md:block" ref={enrollmentRef}>

              {/* Keyframes for the revolving comet border */}
              <style>{`
                @property --enroll-angle {
                  syntax: '<angle>';
                  initial-value: 0deg;
                  inherits: false;
                }
                @keyframes spin-enroll-border {
                  to { --enroll-angle: 360deg; }
                }
                .enroll-animated-border {
                  background: conic-gradient(
                    from var(--enroll-angle),
                    transparent   0deg,
                    transparent 270deg,
                    #6366f1     300deg,
                    #3b82f6     330deg,
                    #93c5fd     348deg,
                    transparent 360deg
                  );
                  animation: spin-enroll-border 2.5s linear infinite;
                }
              `}</style>

              {/* Animated border wrapper: 2px gradient ring */}
              <div className="relative p-[2px] rounded-lg">
                {/* Permanent subtle base border */}
                <div className="absolute inset-0 rounded-lg bg-gray-200" />
                {/* Comet arc on top */}
                <div className="enroll-animated-border absolute inset-0 rounded-lg" />
                {/* White fill masks the spinning layer */}
                <div className="absolute inset-[2px] rounded-[6px] bg-white" />

                <button
                  id={!isMobile ? "tour-current-program" : undefined}
                  onClick={() => setIsEnrollmentOpen(!isEnrollmentOpen)}
                  className="relative flex items-center rounded-[6px] bg-white hover:bg-gray-50 transition-colors overflow-hidden w-full"
                >
                  {/* Left: logo + labels */}
                  <div className="flex items-center gap-2 px-3 py-1.5">
                    <div className="w-8 h-8 flex-shrink-0">
                      <img
                        src={UNIVERSITY_LOGOS[currentEnrollment.university]}
                        alt="University Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex flex-col items-start text-left">
                      <span className="text-[10px] text-gray-500 font-medium leading-tight">Selected Program</span>
                      <span className="text-xs font-semibold text-gray-900 truncate leading-tight">
                        {stripUniversityFromProgramName(currentEnrollment.program_name)}
                      </span>
                    </div>
                  </div>

                  {/* Vertical separator */}
                  <div className="self-stretch w-px bg-gray-200 my-1.5" />

                  {/* Right: chevron — vertically centered */}
                  <div className="flex items-center justify-center px-3 self-stretch">
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-gray-500 transition-transform duration-200 ${isEnrollmentOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>
              </div>

              {/* Enrollment Dropdown Menu */}
              {isEnrollmentOpen && (
                <div className="absolute left-0 mt-2  bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-[500px] overflow-y-auto z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700">
                      My Enrollments ({enrollments.length})
                    </h3>
                  </div>

                  {enrollments.map((enrollment) => {
                    const isActive = currentEnrollment.enrol_id === enrollment.enrol_id;

                    return (
                      <button
                        key={enrollment.enrol_id}
                        onClick={() => handleEnrollmentChange(enrollment)}
                        className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors ${isActive ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                          }`}
                      >
                        <div className="w-8 h-8 flex-shrink-0">
                          <img
                            src={UNIVERSITY_LOGOS[enrollment.university]}
                            alt="University Logo"
                            className="w-full h-full object-contain"
                          />
                        </div>

                        <div className="flex-1 text-left">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {stripUniversityFromProgramName(enrollment.program_name)}
                              </p>
                            </div>
                            {/* {getStatusBadge(enrollment.status, enrollment.is_completed)} */}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Search - hidden on mobile for space */}
          <div className="flex-1 min-w-0 relative hidden sm:block">
            {/* Search content */}
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex min-w-0 items-center gap-2">
          {/* Center Actions Group (Mobile: Absolute Center, Desktop: Flow) */}
          <div className="absolute md:static left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:transform-none flex items-center gap-2 z-20">
            {/* Conditionally render Share Feedback button instead of Upgrade Program */}
            {!shouldHideUpgradeAndNotifications && !isLocked && (
              <Button
                id="tour-dashboard-feedback"
                onClick={() => {
                  setShowFeedbackModal(true);
                }}
                title="Dashboard Feedback"
                className={`relative h-10 rounded-full font-semibold text-sm transition-all duration-500
            hover:scale-110 hover:shadow-xl active:scale-95 group overflow-hidden
            bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-500 hover:to-indigo-700
            text-white border-none shadow-lg animate-gradient-glow flex items-center justify-center
            ${isBothVisible
                    ? "w-10 px-0 md:w-10 md:px-0 lg:w-auto lg:px-4 lg:gap-2"   /* mobile=icon, md=icon, lg+=full */
                    : "px-4 gap-2 md:w-10 md:px-0 lg:w-auto lg:px-4 lg:gap-2" /* mobile=full, md=icon, lg+=full */
                  }
            `}
              >
                {/* Animated background shimmer */}
                <div
                  className="absolute inset-0 opacity-100 transition-opacity duration-700"
                  style={{
                    background:
                      "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)",
                    transform: "translateX(-100%)",
                    animation: "shimmer 2s infinite",
                  }}
                />

                {/* Floating sparkles */}
                <div className="absolute inset-0 opacity-100 transition-opacity duration-500">
                  <div
                    className="absolute w-1 h-1 bg-white rounded-full animate-sparkle-1"
                    style={{ top: "15%", left: "20%" }}
                  />
                  <div
                    className="absolute w-0.5 h-0.5 bg-white rounded-full animate-sparkle-2"
                    style={{ top: "70%", left: "80%" }}
                  />
                  <div
                    className="absolute w-1 h-1 bg-white rounded-full animate-sparkle-3"
                    style={{ top: "40%", left: "70%" }}
                  />
                </div>

                <MessageSquare className="w-4 h-4 transition-all duration-500 group-hover:scale-125 group-hover:rotate-12 relative z-20 text-white flex-shrink-0" />

                {/* Text:
                    - lg+: always visible
                    - md-lg: always hidden
                    - mobile: hidden when both buttons visible */}
                <span className={`relative z-10 whitespace-nowrap md:hidden lg:inline ${isBothVisible ? "hidden" : ""}`}>
                  Dashboard Feedback
                </span>

                {/* Pulsing ring */}
                <div className="absolute -inset-0.5 rounded-full opacity-100 transition-opacity duration-500">
                  <div className="w-full h-full rounded-full border border-purple-300/50 animate-pulse-ring" />
                </div>
              </Button>
            )}
            {showExpectationButton && !isLocked && (
              <Button
                onClick={() => setExpectationsModalOpen(true)}
                title="Student Expectations"
                className={`relative h-10 rounded-full font-semibold text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center animate-pulse
                  ${isBothVisible
                    ? "px-4 gap-2 whitespace-nowrap md:w-10 md:px-0 md:justify-center lg:w-auto lg:px-4 lg:gap-2"   /* mobile=full, md=icon, lg+=full */
                    : "px-4 gap-2 whitespace-nowrap md:w-10 md:px-0 md:justify-center lg:w-auto lg:px-4 lg:gap-2"  /* same */
                  }
                `}
              >
                <FileText className="w-4 h-4 flex-shrink-0" />
                {/* Text:
                    - lg+: always visible
                    - md-lg: always hidden
                    - mobile: always visible for expectations (even when both shown) */}
                <span className="md:hidden lg:inline whitespace-nowrap">
                  Student Expectations
                </span>
              </Button>
            )}
          </div>
          {/* Desktop Notification Icon - Hidden on mobile and when conditions met */}

          {!shouldHideUpgradeAndNotifications && (
            <div className="relative hidden md:block">
              <Button
                variant="ghost"
                size="icon"
                aria-haspopup="true"
                aria-expanded={showNotifications}
              >
                <img
                  src="https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/Student-Dashboard/star.png"
                  className="w-6 h-6 mr-1"
                  alt=""
                />
                {/* <HandCoins  className="w-5 h-5 mr-2" /> */}
                {coinsdata}
              </Button>
            </div>
          )}
          {!shouldHideUpgradeAndNotifications && (
            <div className="relative hidden md:block" ref={bellRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNotifications(!showNotifications)}
                aria-haspopup="true"
                aria-expanded={showNotifications}
              >
                <Bell className="w-5 h-5" />
                {countNoti > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-red-600 text-white text-xs">
                    {countNoti}
                  </Badge>
                )}
              </Button>

              {/* Notifications Popup for desktop */}
              {/* {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 max-w-xs bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200 font-semibold text-gray-900">
                    Notifications
                  </div>
                  <ul className="max-h-60 overflow-y-auto">
                    {notifications.map((notif) => (
                      <li
                        key={notif.id}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                        onClick={() => {
                          checkdClick(notif.id);
                          if (notif.notification_url) {
                            window.open(notif.notification_url, "_blank");
                          }
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="max-w-[75%] text-gray-900 font-semibold text-sm truncate">
                            {notif.heading_text || notif.message}
                          </div>
                          {!notif.checked && (
                            <div>
                              <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 truncate">
                          {notif.notification_text}
                        </div>
                        <div className="text-xs font-bold text-blue-700 mt-1">
                          {notif.duration} ago
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )} */}

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 max-w-xs bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200 font-semibold text-gray-900">
                    Notifications
                  </div>
                  {notifications.length > 0 ? (
                    <ul className="max-h-60 overflow-y-auto">
                      {notifications.map((notif) => (
                        <li
                          key={notif.id}
                          className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            checkdClick(notif.id);
                            if (notif.notification_url) {
                              window.open(notif.notification_url, "_blank");
                            }
                          }}
                        >
                          <div className="flex justify-between items-center">
                            <div className="max-w-[75%] text-gray-900 font-semibold text-sm truncate">
                              {notif.heading_text || notif.message}
                            </div>
                            {!notif.checked && (
                              <div>
                                <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            {notif.notification_text}
                          </div>
                          <div className="text-xs font-bold text-blue-700 mt-1">
                            {notif.duration} ago
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No notifications available
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


          {/* Mobile Profile Avatar with Dropdown */}
          <div className="relative z-[200]">
            <button
              ref={profileButtonRef}
              onClick={toggleProfileMenu}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              aria-expanded={isProfileMenuOpen}
              aria-haspopup="true"
            >
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-gray-100 text-gray-600">
                  {(localStorage.getItem("username")?.charAt(0) || "") +
                    (localStorage.getItem("lastname")?.charAt(0) || "")}
                </AvatarFallback>
              </Avatar>
              <ChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isProfileMenuOpen ? "rotate-180" : ""
                  }`}
              />
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileMenuOpen && (
              <div
                ref={profileMenuRef}
                className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[200] py-2"
              >
                {/* User Info Header */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-gray-100 text-gray-600">
                        {(localStorage.getItem("username")?.charAt(0) || "") +
                          (localStorage
                            .getItem("lastname")
                            ?.split(" ")
                            .slice(-1)[0]
                            ?.charAt(0) || "")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {localStorage.getItem("username") +
                          " " +
                          localStorage.getItem("lastname")}
                      </p>
                      <p className="text-xs mb-0 text-gray-500 truncate">
                        {localStorage.getItem("email")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  {/* Mobile-only Notifications - also conditionally hide */}

                  {isMobile && !shouldHideUpgradeAndNotifications && (
                    <>
                      <button
                        onClick={() => handleProfileMenuClick("notifications")}
                        className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Bell className="w-4 h-4 text-gray-500" />
                          <span>Notifications</span>
                        </div>
                        {countNoti > 0 && (
                          <Badge className="w-5 h-5 p-0 flex items-center justify-center bg-red-600 text-white text-xs">
                            {countNoti}
                          </Badge>
                        )}
                      </button>
                      <hr className="my-2 border-gray-200" />
                    </>
                  )}

                  {/* Dashboard Tour — visible on all screen sizes in the profile menu */}
                  {isMobile && (
                    <>
                      <button
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleRestartTour();
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-indigo-50 transition-colors"
                      >
                        <Compass className="w-4 h-4 text-indigo-500" />
                        <div className="text-left">
                          <span className="block font-medium">Dashboard Tour</span>
                          <span className="block text-[10px] text-gray-400 leading-tight">Restart the guided walkthrough</span>
                        </div>
                      </button>
                      <hr className="my-2 border-gray-200" />
                    </>
                  )}

                  <button
                    onClick={() => handleProfileMenuClick("profile")}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-500" />
                    <span>My Profile</span>
                  </button>

                  <button
                    onClick={() => handleProfileMenuClick("policy")}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span>Student Policy</span>
                  </button>

                  <button
                    onClick={() => handleProfileMenuClick("password")}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <KeyRound className="w-4 h-4 text-gray-500" />
                    <span>Change Password</span>
                  </button>

                  <hr className="my-2 border-gray-200" />

                  <button
                    onClick={() => {
                      LogOutUser("token", "accredian.com");
                    }}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Enrollment Bar */}
      {isMobile && !loadingEnrollments && enrollments.length > 0 && currentEnrollment && (
        <div ref={mobileEnrollmentRef} className="relative z-[200] bg-white px-4 py-2">

          {/* Keyframes for the revolving comet border — mobile */}
          <style>{`
            @property --enroll-mob-angle {
              syntax: '<angle>';
              initial-value: 0deg;
              inherits: false;
            }
            @keyframes spin-enroll-mob-border {
              to { --enroll-mob-angle: 360deg; }
            }
            .enroll-mob-animated-border {
              background: conic-gradient(
                from var(--enroll-mob-angle),
                transparent   0deg,
                transparent 270deg,
                #6366f1     300deg,
                #3b82f6     330deg,
                #93c5fd     348deg,
                transparent 360deg
              );
              animation: spin-enroll-mob-border 2.5s linear infinite;
            }
          `}</style>

          {/* Animated border wrapper */}
          <div className="relative p-[2px] rounded-2xl">
            {/* Permanent subtle base border */}
            <div className="absolute inset-0 rounded-2xl bg-gray-200" />
            {/* Comet arc on top */}
            <div className="enroll-mob-animated-border absolute inset-0 rounded-2xl" />
            {/* White fill masks the spinning layer */}
            <div className="absolute inset-[2px] rounded-[14px] bg-white" />

            <button
              id={isMobile ? "tour-current-program" : undefined}
              onClick={() => setIsEnrollmentOpen(!isEnrollmentOpen)}
              className={`relative w-full px-5 py-3 flex items-center justify-between bg-white transition-all duration-200 rounded-[14px] hover:bg-gray-50`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-7 h-7 flex-shrink-0">
                  <img
                    src={UNIVERSITY_LOGOS[currentEnrollment.university]}
                    alt="University Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-0.5">
                    Active Program
                  </span>
                  <div className="flex items-center text-sm font-bold text-gray-900 truncate w-full">
                    <span className="truncate">
                      {stripUniversityFromProgramName(currentEnrollment.program_name)}
                    </span>
                    <span className="mx-1 text-gray-300 font-light">|</span>
                    <span className="text-gray-500 font-semibold uppercase shrink-0">
                      {currentEnrollment.university}
                    </span>
                  </div>
                </div>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ml-2 flex-shrink-0 ${isEnrollmentOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {isEnrollmentOpen && (
            <div className="absolute top-full left-4 right-4 border border-gray-200 bg-white shadow-2xl rounded-b-2xl max-h-[300px] overflow-y-auto z-[201]">
              {enrollments.map((enrollment) => {
                const isActive = currentEnrollment.enrol_id === enrollment.enrol_id;
                return (
                  <button
                    type="button"
                    key={enrollment.enrol_id}
                    onClick={(e) => {
                      e.preventDefault();
                      handleEnrollmentChange(enrollment);
                    }}
                    className={`w-full px-5 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${isActive ? "bg-blue-50/50" : ""
                      }`}
                  >
                    <div className="w-6 h-6 flex-shrink-0 mt-0.5">
                      <img
                        src={UNIVERSITY_LOGOS[enrollment.university]}
                        alt="University Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`text-xs font-semibold ${isActive ? "text-blue-700" : "text-gray-900"}`}>
                        {stripUniversityFromProgramName(enrollment.program_name)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Click-away backdrop when mobile enrollment dropdown is open */}
      {isEnrollmentOpen && isMobile && (
        <div
          className="fixed inset-0 z-[199] bg-black/20"
          onClick={() => setIsEnrollmentOpen(false)}
        />
      )}

      {/* ── Floating Dashboard Tour Side Drawer ── */}
      <style>{`
        @keyframes tour-slide-in {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .tour-drawer-open { animation: tour-slide-in 0.32s cubic-bezier(0.34,1.4,0.64,1) both; }

        @keyframes tour-tab-breathe {
          0%, 100% { box-shadow: 2px 0 12px rgba(59,130,246,0.18); }
          50%       { box-shadow: 2px 0 20px rgba(59,130,246,0.35); }
        }
        .tour-tab-glow { animation: tour-tab-breathe 3s ease-in-out infinite; }
      `}</style>

      {/* Backdrop when drawer is open */}
      {isTourMenuOpen && (
        <div
          className="fixed inset-0 z-[490] bg-black/10 md:hidden"
          onClick={() => setIsTourMenuOpen(false)}
        />
      )}

      {/* The side tab + drawer (All screen sizes) */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-[500] flex items-center">

        {/* Slide-out panel */}
        {isTourMenuOpen && (
          <div className="tour-drawer-open w-60 bg-white rounded-l-2xl shadow-xl border border-gray-200 border-r-0 overflow-hidden flex flex-col">
            {/* Panel header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 flex-shrink-0">
                <Compass className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 font-semibold text-sm leading-tight">Guided Tour</p>
                <p className="text-gray-400 text-[10px] leading-tight mt-0.5">Interactive walkthrough</p>
              </div>
              <button
                onClick={() => setIsTourMenuOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Panel body */}
            <div className="px-4 py-4 flex flex-col gap-3">
              <p className="text-xs text-gray-500 leading-relaxed">
                Let us walk you through the key features of your dashboard step by step.
              </p>

              <button
                onClick={() => {
                  setIsTourMenuOpen(false);
                  handleRestartTour();
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl
                           bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold
                           active:scale-95 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Compass className="w-4 h-4" />
                Start Dashboard Tour
              </button>
            </div>
          </div>
        )}

        {/* Vertical tab trigger */}
        <button
          onClick={() => setIsTourMenuOpen(!isTourMenuOpen)}
          title="Dashboard Tour"
          className={`
            tour-tab-glow
            flex flex-col items-center justify-center gap-1.5
            w-7 py-5 
            transition-all duration-200 active:scale-95
            ${isTourMenuOpen
              ? 'bg-gray-100 border border-l-0 border-r-0 border-gray-200 text-gray-500 shadow-none rounded-none z-[501]'
              : 'bg-white border border-r-0 border-gray-200 text-blue-600 hover:bg-gray-50 md:bg-blue-600 md:border-blue-700 md:text-white md:hover:bg-blue-700 shadow-md rounded-l-xl'}
          `}
        >
          <Compass className="w-3.5 h-3.5 flex-shrink-0" />
          <span
            className="text-[9px] font-bold tracking-widest uppercase leading-none"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', letterSpacing: '0.12em' }}
          >
            Tour
          </span>
        </button>
      </div>

      {/* Notifications Modal for Mobile - also conditionally hide */}
      {/* {isMobile &&
        showNotificationsModal &&
        !shouldHideUpgradeAndNotifications && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Notifications
                </h3>
                <button
                  onClick={() => setShowNotificationsModal(false)}
                  aria-label="Close notifications modal"
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <ul className="max-h-72 overflow-y-auto p-4 space-y-3">
                {notifications.map((notif) => (
                  <li
                    key={notif.id}
                    className="border rounded-md p-3 bg-gray-50 text-gray-800 cursor-pointer"
                    onClick={() => {
                      checkdClick(notif.id);
                      if (notif.notification_url) {
                        window.open(notif.notification_url, "_blank");
                      }
                      setShowNotificationsModal(false);
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div className="max-w-[75%] font-semibold text-sm truncate">
                        {notif.heading_text || notif.message}
                      </div>
                      {!notif.checked && (
                        <div>
                          <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 truncate">
                      {notif.notification_text}
                    </div>
                    <div className="text-xs font-bold text-blue-700 mt-1">
                      {notif.duration} ago
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )} */}

      {isMobile &&
        showNotificationsModal &&
        !shouldHideUpgradeAndNotifications && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Notifications
                </h3>
                <button
                  onClick={() => setShowNotificationsModal(false)}
                  aria-label="Close notifications modal"
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              {notifications.length > 0 ? (
                <ul className="max-h-72 overflow-y-auto p-4 space-y-3">
                  {notifications.map((notif) => (
                    <li
                      key={notif.id}
                      className="border rounded-md p-3 bg-gray-50 text-gray-800 cursor-pointer"
                      onClick={() => {
                        checkdClick(notif.id);
                        if (notif.notification_url) {
                          window.open(notif.notification_url, "_blank");
                        }
                        setShowNotificationsModal(false);
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div className="max-w-[75%] font-semibold text-sm truncate">
                          {notif.heading_text || notif.message}
                        </div>
                        {!notif.checked && (
                          <div>
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {notif.notification_text}
                      </div>
                      <div className="text-xs font-bold text-blue-700 mt-1">
                        {notif.duration} ago
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No notifications available
                </div>
              )}
            </div>
          </div>
        )}

      <FraudNotificationStrip />

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackForm onClose={() => setShowFeedbackModal(false)} />
      )}

      {/* Security Acknowledgment Modal */}
      <SecurityAdvisoryModal
        open={acknowledgementModalOpen}
        setOpen={setAcknowledgementModalOpen}
        onContact={() => { }}
      />

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes sparkle-1 {
          0%,
          100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes sparkle-2 {
          0%,
          100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes sparkle-3 {
          0%,
          100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-sparkle-1 {
          animation: sparkle-1 1.5s ease-in-out infinite;
        }

        .animate-sparkle-2 {
          animation: sparkle-2 1.8s ease-in-out infinite 0.3s;
        }

        .animate-sparkle-3 {
          animation: sparkle-3 1.6s ease-in-out infinite 0.6s;
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.1);
            opacity: 0;
          }
        }

        .animate-pulse-ring {
          animation: pulse-ring 1.5s ease-out infinite;
        }
      `}</style>


      <ExpectationsModal
        isOpen={expectationsModalOpen}
        updateExpectationsCount={updateExpectationsCount}
        onClose={() => setExpectationsModalOpen(false)}
        userId={localStorage.getItem('user_id')}
      />
    </>
  );
};

export default TopBar;




// import {
//   Search,
//   Bell,
//   ArrowUp,
//   Zap,
//   Menu,
//   X,
//   User,
//   FileText,
//   KeyRound,
//   LogOut,
//   ChevronDown,
//   CirclePoundSterling,
//   HandCoins,
// } from "lucide-react";
// import { useState, useRef, useEffect } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";
// import { generateApiKey } from "../../../../helpers/common.helpers";
// import FraudNotificationStrip from "../../../Dashboard/FraudNotificationStrip";

// // Custom Button Component to replace Shadcn Button
// const Button = ({
//   variant = "default",
//   size = "default",
//   className = "",
//   children,
//   ...props
// }) => {
//   const baseClasses =
//     "inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

//   const variantClasses = {
//     default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
//     ghost: "hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-300",
//     outline:
//       "border border-gray-300 bg-transparent hover:bg-gray-50 focus:ring-gray-300",
//   };

//   const sizeClasses = {
//     default: "h-10 px-4 py-2",
//     sm: "h-9 rounded-md px-3",
//     lg: "h-11 rounded-md px-8",
//     icon: "h-10 w-10",
//   };

//   return (
//     <button
//       className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
//       {...props}
//     >
//       {children}
//     </button>
//   );
// };

// // Custom Badge Component to replace Shadcn Badge
// const Badge = ({ children, className = "" }) => {
//   return (
//     <span
//       className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white ${className}`}
//     >
//       {children}
//     </span>
//   );
// };

// // Custom Avatar Components to replace Shadcn Avatar
// const Avatar = ({ className = "", children }) => {
//   return (
//     <div
//       className={`relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full ${className}`}
//     >
//       {children}
//     </div>
//   );
// };

// const AvatarFallback = ({ className = "", children }) => {
//   return (
//     <div
//       className={`flex h-full w-full items-center justify-center rounded-full bg-gray-100 text-gray-600 ${className}`}
//     >
//       {children}
//     </div>
//   );
// };

// // Custom Input Component to replace Shadcn Input
// const Input = ({ className = "", ...props }) => {
//   return (
//     <input
//       className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
//       {...props}
//     />
//   );
// };

// // TopBar Component with API integration
// const TopBar = ({ isMobile, onMobileMenuToggle }) => {
//   const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
//   const [showNotifications, setShowNotifications] = useState(false);
//   const [showNotificationsModal, setShowNotificationsModal] = useState(false);
//   const [notifications, setNotifications] = useState([]);
//   const [countNoti, setCountNoti] = useState(0);
//   const [coinsdata, setCoindata] = useState(0);
//   const profileMenuRef = useRef(null);
//   const profileButtonRef = useRef(null);
//   const bellRef = useRef(null);
//   const navigate = useNavigate();
//   const location = useLocation();
//   const isLocked = localStorage.getItem("status") !== "active";

//   const searchParams = new URLSearchParams(location.search);
//   const tabParam = searchParams.get("tab");
//   const shouldHideUpgradeAndNotifications =
//     tabParam === "my-courses" || tabParam === "my-applications";

//   // Fetch notifications from API
//   const fetchNotificationData = async () => {
//     try {
//       const res = await axios({
//         method: "post",
//         headers: {
//           "Access-Control-Allow-Origin": "*",
//           "Content-Type": "application/json",
//           // Assuming generateApiKey is defined elsewhere if needed
//           "page-index": generateApiKey(),
//         },
//         url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/fetchnotification`,
//         data: {
//           enrol_id: Number(localStorage.getItem("enrol_id")),
//         },
//       });
//       setNotifications(res.data.notification || []);
//       setCountNoti(res.data.unchecked_count || 0);
//     } catch (error) {
//       console.error("Failed to fetch notifications", error);
//     }
//   };

//   const fetchcoindata = async () => {
//     await axios({
//       method: "post",
//       headers: {
//         "Access-Control-Allow-Origin": "*",
//         "Content-Type": "application/json",
//         "page-index": generateApiKey(),
//       },
//       url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/fetchstudentcoins`,
//       data: {
//         enrol_id: localStorage.getItem("enrol_id"),
//       },
//     }).then((res) => {
//       setCoindata(res.data.toString());
//       localStorage.setItem("coins", res.data.toString());
//     });
//   };

//   // Mark notification as checked
//   const checkdClick = async (id) => {
//     try {
//       const res = await axios({
//         method: "post",
//         headers: {
//           "Access-Control-Allow-Origin": "*",
//           "Content-Type": "application/json",
//           "page-index": generateApiKey(),
//         },
//         url: `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/headerdetails/updatenotification_status`,
//         data: {
//           enrol_id: 38626,
//           id: id,
//         },
//       });
//       setCountNoti(res.data.unchecked_count || 0);
//       // Optionally update the checked state locally
//       setNotifications((prev) =>
//         prev.map((n) => (n.id === id ? { ...n, checked: 1 } : n))
//       );
//     } catch (error) {
//       console.error("Failed to update notification status", error);
//     }
//   };

//   useEffect(() => {
//     fetchNotificationData();
//     fetchcoindata();
//   }, []);

//   // Close profile menu and notification popup when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         profileMenuRef.current &&
//         profileButtonRef.current &&
//         !profileMenuRef.current.contains(event.target) &&
//         !profileButtonRef.current.contains(event.target)
//       ) {
//         setIsProfileMenuOpen(false);
//       }
//       if (
//         bellRef.current &&
//         !bellRef.current.contains(event.target) &&
//         !isMobile
//       ) {
//         setShowNotifications(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [isMobile]);

//   const toggleProfileMenu = () => {
//     setIsProfileMenuOpen(!isProfileMenuOpen);
//   };

//   const LogOutUser = (name, domain) => {
//     document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
//     localStorage.clear();
//     window.location.href = "https://accredian.com";
//   };

//   const handleProfileMenuClick = (action) => {
//     console.log(`${action} clicked`);
//     setIsProfileMenuOpen(false);

//     switch (action) {
//       case "notifications":
//         if (isMobile) {
//           setShowNotificationsModal(true);
//         }
//         break;
//       case "profile":
//         navigate("/Profile");
//         break;
//       case "policy":
//         window.open(
//           "https://accredian.com/policies/accredian-policy?_gl=1*1lt8sky*_gcl_au*MjI2MzYxNzk4LjE3NTg3ODc3NjMuOTI2NzU2OTg2LjE3NTkzMTYxODUuMTc1OTMxNjE4NA..*_ga*MTk2NDAwMDQ3MC4xNzUxMDA1Njc3*_ga_SWCZ0464KL*czE3NTkzMTYxMzEkbzMxNCRnMSR0MTc1OTMxNjE4NCRqNyRsMCRoMA..",
//           "_blank"
//         );
//         break;
//       case "password":
//         navigate("/ChangePassword");
//         break;
//       case "logout":
//         LogOutUser("token", "accredian.com");
//         // Add logout logic here as needed
//         break;
//       default:
//         break;
//     }
//   };

//   return (
//     <>


//       <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200 shadow-sm">
//         {/* Left side - Mobile menu button (only visible on mobile) */}
//         <div className="flex items-center gap-4">
//           {isMobile && (
//             <Button
//               variant="ghost"
//               size="icon"
//               onClick={onMobileMenuToggle}
//               className="md:hidden p-2 hover:bg-gray-100 transition-colors"
//             >
//               <Menu className="w-20 h-20" />
//             </Button>
//           )}

//           {/* Search - hidden on mobile for space */}
//           <div className="flex-1 min-w-0 relative hidden sm:block">
//             {/* Search content */}
//           </div>
//         </div>

//         {/* Right side actions */}
//         <div className="flex min-w-0 items-center gap-4">
//           {/* Conditionally render Upgrade Program button */}
//           {!shouldHideUpgradeAndNotifications && !isLocked && (
//             //   <Button

//             //  onClick={()=>{ navigate("/Program-Upgrade");}}
//             //     className="relative h-10 px-4 rounded-full font-semibold text-sm transition-all duration-500
//             //              hover:scale-110 hover:shadow-xl active:scale-95 group overflow-hidden
//             //              bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-500 hover:to-indigo-700
//             //              text-white border-none shadow-lg animate-gradient-glow"
//             //   >
//             //     {/* Animated background shimmer */}
//             //     <div
//             //       className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
//             //       style={{
//             //         background:
//             //           "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)",
//             //         transform: "translateX(-100%)",
//             //         animation: "shimmer 2s infinite",
//             //       }}
//             //     />

//             //     {/* Floating sparkles */}
//             //     <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
//             //       <div
//             //         className="absolute w-1 h-1 bg-white rounded-full animate-sparkle-1"
//             //         style={{ top: "15%", left: "20%" }}
//             //       />
//             //       <div
//             //         className="absolute w-0.5 h-0.5 bg-white rounded-full animate-sparkle-2"
//             //         style={{ top: "70%", left: "80%" }}
//             //       />
//             //       <div
//             //         className="absolute w-1 h-1 bg-white rounded-full animate-sparkle-3"
//             //         style={{ top: "40%", left: "70%" }}
//             //       />
//             //     </div>

//             //     <Zap className="w-4 h-4 mr-2 transition-all duration-500 group-hover:scale-125 group-hover:rotate-12 relative z-10" />
//             //     <span className="relative z-10 transition-all duration-500 group-hover:tracking-wide hidden sm:inline">
//             //       Upgrade Program
//             //     </span>
//             //     <span className="relative z-10 transition-all duration-500 group-hover:tracking-wide sm:hidden">
//             //       Upgrade Program
//             //     </span>
//             //     <ArrowUp className="w-4 h-4 ml-2 transition-all duration-500 group-hover:translate-y-[-2px] group-hover:scale-110 relative z-10" />

//             //     {/* Pulsing ring effect */}
//             //     <div className="absolute -inset-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500">
//             //       <div className="w-full h-full rounded-full border border-purple-300/50 animate-pulse-ring" />
//             //     </div>
//             //   </Button>
//             <></>
//           )}

//           {/* Desktop Notification Icon - Hidden on mobile and when conditions met */}

//           {!shouldHideUpgradeAndNotifications && (
//             <div className="relative hidden md:block">
//               <Button
//                 variant="ghost"
//                 size="icon"
//                 aria-haspopup="true"
//                 aria-expanded={showNotifications}
//               >
//                 <img
//                   src="https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/Student-Dashboard/star.png"
//                   className="w-6 h-6 mr-1"
//                   alt=""
//                 />
//                 {/* <HandCoins  className="w-5 h-5 mr-2" /> */}
//                 {coinsdata}
//               </Button>
//             </div>
//           )}
//           {!shouldHideUpgradeAndNotifications && (
//             <div className="relative hidden md:block" ref={bellRef}>
//               <Button
//                 variant="ghost"
//                 size="icon"
//                 onClick={() => setShowNotifications(!showNotifications)}
//                 aria-haspopup="true"
//                 aria-expanded={showNotifications}
//               >
//                 <Bell className="w-5 h-5" />
//                 {countNoti > 0 && (
//                   <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-red-600 text-white text-xs">
//                     {countNoti}
//                   </Badge>
//                 )}
//               </Button>

//               {/* Notifications Popup for desktop */}
//               {/* {showNotifications && (
//                 <div className="absolute right-0 mt-2 w-80 max-w-xs bg-white rounded-lg shadow-lg border border-gray-200 z-50">
//                   <div className="p-4 border-b border-gray-200 font-semibold text-gray-900">
//                     Notifications
//                   </div>
//                   <ul className="max-h-60 overflow-y-auto">
//                     {notifications.map((notif) => (
//                       <li
//                         key={notif.id}
//                         className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
//                         onClick={() => {
//                           checkdClick(notif.id);
//                           if (notif.notification_url) {
//                             window.open(notif.notification_url, "_blank");
//                           }
//                         }}
//                       >
//                         <div className="flex justify-between items-center">
//                           <div className="max-w-[75%] text-gray-900 font-semibold text-sm truncate">
//                             {notif.heading_text || notif.message}
//                           </div>
//                           {!notif.checked && (
//                             <div>
//                               <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
//                             </div>
//                           )}
//                         </div>
//                         <div className="text-xs text-gray-500 mt-1 truncate">
//                           {notif.notification_text}
//                         </div>
//                         <div className="text-xs font-bold text-blue-700 mt-1">
//                           {notif.duration} ago
//                         </div>
//                       </li>
//                     ))}
//                   </ul>
//                 </div>
//               )} */}

//               {showNotifications && (
//                 <div className="absolute right-0 mt-2 w-80 max-w-xs bg-white rounded-lg shadow-lg border border-gray-200 z-50">
//                   <div className="p-4 border-b border-gray-200 font-semibold text-gray-900">
//                     Notifications
//                   </div>
//                   {notifications.length > 0 ? (
//                     <ul className="max-h-60 overflow-y-auto">
//                       {notifications.map((notif) => (
//                         <li
//                           key={notif.id}
//                           className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
//                           onClick={() => {
//                             checkdClick(notif.id);
//                             if (notif.notification_url) {
//                               window.open(notif.notification_url, "_blank");
//                             }
//                           }}
//                         >
//                           <div className="flex justify-between items-center">
//                             <div className="max-w-[75%] text-gray-900 font-semibold text-sm truncate">
//                               {notif.heading_text || notif.message}
//                             </div>
//                             {!notif.checked && (
//                               <div>
//                                 <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
//                               </div>
//                             )}
//                           </div>
//                           <div className="text-xs text-gray-500 mt-1 truncate">
//                             {notif.notification_text}
//                           </div>
//                           <div className="text-xs font-bold text-blue-700 mt-1">
//                             {notif.duration} ago
//                           </div>
//                         </li>
//                       ))}
//                     </ul>
//                   ) : (
//                     <div className="px-4 py-8 text-center text-gray-500 text-sm">
//                       No notifications available
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Mobile Profile Avatar with Dropdown */}
//           <div className="relative">
//             <button
//               ref={profileButtonRef}
//               onClick={toggleProfileMenu}
//               className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20"
//               aria-expanded={isProfileMenuOpen}
//               aria-haspopup="true"
//             >
//               <Avatar className="w-8 h-8">
//                 <AvatarFallback className="bg-gray-100 text-gray-600">
//                   {(localStorage.getItem("username")?.charAt(0) || "") +
//                     (localStorage.getItem("lastname")?.charAt(0) || "")}
//                 </AvatarFallback>
//               </Avatar>
//               <ChevronDown
//                 className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
//                   isProfileMenuOpen ? "rotate-180" : ""
//                 }`}
//               />
//             </button>

//             {/* Profile Dropdown Menu */}
//             {isProfileMenuOpen && (
//               <div
//                 ref={profileMenuRef}
//                 className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2"
//               >
//                 {/* User Info Header */}
//                 <div className="px-4 py-3 border-b border-gray-200">
//                   <div className="flex items-center gap-3">
//                     <Avatar className="w-10 h-10">
//                       <AvatarFallback className="bg-gray-100 text-gray-600">
//                         {(localStorage.getItem("username")?.charAt(0) || "") +
//                           (localStorage
//                             .getItem("lastname")
//                             ?.split(" ")
//                             .slice(-1)[0]
//                             ?.charAt(0) || "")}
//                       </AvatarFallback>
//                     </Avatar>
//                     <div className="flex-1 min-w-0">
//                       <p className="text-sm font-medium text-gray-900 truncate">
//                         {localStorage.getItem("username") +
//                           " " +
//                           localStorage.getItem("lastname")}
//                       </p>
//                       <p className="text-xs mb-0 text-gray-500 truncate">
//                         {localStorage.getItem("email")}
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Menu Items */}
//                 <div className="py-2">
//                   {/* Mobile-only Notifications - also conditionally hide */}

//                   {isMobile && !shouldHideUpgradeAndNotifications && (
//                     <>
//                       <button
//                         onClick={() => handleProfileMenuClick("notifications")}
//                         className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
//                       >
//                         <div className="flex items-center gap-3">
//                           <Bell className="w-4 h-4 text-gray-500" />
//                           <span>Notifications</span>
//                         </div>
//                         {countNoti > 0 && (
//                           <Badge className="w-5 h-5 p-0 flex items-center justify-center bg-red-600 text-white text-xs">
//                             {countNoti}
//                           </Badge>
//                         )}
//                       </button>
//                       <hr className="my-2 border-gray-200" />
//                     </>
//                   )}

//                   <button
//                     onClick={() => handleProfileMenuClick("profile")}
//                     className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
//                   >
//                     <User className="w-4 h-4 text-gray-500" />
//                     <span>My Profile</span>
//                   </button>

//                   <button
//                     onClick={() => handleProfileMenuClick("policy")}
//                     className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
//                   >
//                     <FileText className="w-4 h-4 text-gray-500" />
//                     <span>Student Policy</span>
//                   </button>

//                   <button
//                     onClick={() => handleProfileMenuClick("password")}
//                     className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
//                   >
//                     <KeyRound className="w-4 h-4 text-gray-500" />
//                     <span>Change Password</span>
//                   </button>

//                   <hr className="my-2 border-gray-200" />

//                   <button
//                     onClick={() => {
//                       LogOutUser("token", "accredian.com");
//                     }}
//                     className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
//                   >
//                     <LogOut className="w-4 h-4" />
//                     <span>Logout</span>
//                   </button>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Notifications Modal for Mobile - also conditionally hide */}
//       {/* {isMobile &&
//         showNotificationsModal &&
//         !shouldHideUpgradeAndNotifications && (
//           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
//             <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
//               <div className="flex items-center justify-between border-b border-gray-200 p-4">
//                 <h3 className="text-lg font-semibold text-gray-900">
//                   Notifications
//                 </h3>
//                 <button
//                   onClick={() => setShowNotificationsModal(false)}
//                   aria-label="Close notifications modal"
//                   className="text-gray-500 hover:text-gray-700 focus:outline-none"
//                 >
//                   <X className="w-6 h-6" />
//                 </button>
//               </div>
//               <ul className="max-h-72 overflow-y-auto p-4 space-y-3">
//                 {notifications.map((notif) => (
//                   <li
//                     key={notif.id}
//                     className="border rounded-md p-3 bg-gray-50 text-gray-800 cursor-pointer"
//                     onClick={() => {
//                       checkdClick(notif.id);
//                       if (notif.notification_url) {
//                         window.open(notif.notification_url, "_blank");
//                       }
//                       setShowNotificationsModal(false);
//                     }}
//                   >
//                     <div className="flex justify-between items-center">
//                       <div className="max-w-[75%] font-semibold text-sm truncate">
//                         {notif.heading_text || notif.message}
//                       </div>
//                       {!notif.checked && (
//                         <div>
//                           <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
//                         </div>
//                       )}
//                     </div>
//                     <div className="text-xs text-gray-500 mt-1 truncate">
//                       {notif.notification_text}
//                     </div>
//                     <div className="text-xs font-bold text-blue-700 mt-1">
//                       {notif.duration} ago
//                     </div>
//                   </li>
//                 ))}
//               </ul>
//             </div>
//           </div>
//         )} */}

//       {isMobile &&
//         showNotificationsModal &&
//         !shouldHideUpgradeAndNotifications && (
//           <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
//             <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
//               <div className="flex items-center justify-between border-b border-gray-200 p-4">
//                 <h3 className="text-lg font-semibold text-gray-900">
//                   Notifications
//                 </h3>
//                 <button
//                   onClick={() => setShowNotificationsModal(false)}
//                   aria-label="Close notifications modal"
//                   className="text-gray-500 hover:text-gray-700 focus:outline-none"
//                 >
//                   <X className="w-6 h-6" />
//                 </button>
//               </div>
//               {notifications.length > 0 ? (
//                 <ul className="max-h-72 overflow-y-auto p-4 space-y-3">
//                   {notifications.map((notif) => (
//                     <li
//                       key={notif.id}
//                       className="border rounded-md p-3 bg-gray-50 text-gray-800 cursor-pointer"
//                       onClick={() => {
//                         checkdClick(notif.id);
//                         if (notif.notification_url) {
//                           window.open(notif.notification_url, "_blank");
//                         }
//                         setShowNotificationsModal(false);
//                       }}
//                     >
//                       <div className="flex justify-between items-center">
//                         <div className="max-w-[75%] font-semibold text-sm truncate">
//                           {notif.heading_text || notif.message}
//                         </div>
//                         {!notif.checked && (
//                           <div>
//                             <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
//                           </div>
//                         )}
//                       </div>
//                       <div className="text-xs text-gray-500 mt-1 truncate">
//                         {notif.notification_text}
//                       </div>
//                       <div className="text-xs font-bold text-blue-700 mt-1">
//                         {notif.duration} ago
//                       </div>
//                     </li>
//                   ))}
//                 </ul>
//               ) : (
//                 <div className="p-8 text-center text-gray-500 text-sm">
//                   No notifications available
//                 </div>
//               )}
//             </div>
//           </div>
//         )}



//   );
// };

// export default TopBar;
